import { WellImageScoreDTO } from 'business/models/Models';
import ImageRenderer from 'components/image/image';
import React, { useEffect, useRef } from 'react';
import {
  getWellDropScoreType,
  useImagesList,
  useIsFetchingData,
  useMoveSelectedImageIndex,
  useSearchedAlready,
  useSelectedImageIndex,
  useSetSelectedImageIndex,
} from './withImageScoringSortContext';
import LoadingDiv from 'components/generic/loadingDiv';
import AutoScoreRenderer from 'components/scores/autoScoresRendererNonCanvas';
import NoImage from 'components/generic/noImage';
import NoDataFound from 'components/generic/noDataFound';
import { NavigationSource } from 'utils/Global';

interface Props {
  isFocused: boolean;
}

const ITEM_WIDTH = 69;
function calculateVH(screenHeight: number) {
  const slope = 0.01759;
  const intercept = 38.8;
  return slope * screenHeight + intercept;
}

const ThumbnailsListRenderer = (props: Props) => {
  const { isFocused } = props;

  const imagesList: WellImageScoreDTO[] = useImagesList() || [];
  const isFetchingData = useIsFetchingData();
  const selectImage = useSetSelectedImageIndex();
  const selectedImageIndex = useSelectedImageIndex();
  const searchedAlready = useSearchedAlready();
  const moveSelectedIndex = useMoveSelectedImageIndex();
  const images = useImagesList();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to the selected image if it is not in the viewport
    if (containerRef.current && selectedImageIndex !== null && selectedImageIndex >= 0) {
      const container = containerRef.current;
      const selectedImage = container.children[selectedImageIndex];

      if (selectedImage) {
        const containerRect = container.getBoundingClientRect();
        const imageRect = selectedImage.getBoundingClientRect();
        const extraPadding = containerRect.height * 0.1; // 10% of the container height as padding

        if (imageRect.top < containerRect.top + extraPadding) {
          // Scroll up to bring the item into view with extra padding
          container.scrollTop -= containerRect.top - imageRect.top + extraPadding;
        } else if (imageRect.bottom > containerRect.bottom - extraPadding) {
          // Scroll down to bring the item into view with extra padding
          container.scrollTop += imageRect.bottom - containerRect.bottom + extraPadding;
        }
      }
    }
  }, [selectedImageIndex]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isFocused) {
      return;
    }
    const containerWidth = containerRef.current ? containerRef.current.offsetWidth : 0;
    const itemsPerRow = Math.floor(containerWidth / (ITEM_WIDTH + 2)); // + 2px for padding and gap

    switch (event.key) {
      case 'ArrowRight':
        if (images.length > 0) {
          moveSelectedIndex(1);
        }
        break;

      case 'ArrowLeft':
        if (images.length > 0) {
          moveSelectedIndex(-1);
        }
        break;

      case 'ArrowDown':
        if (selectedImageIndex + itemsPerRow < images.length) {
          selectImage(selectedImageIndex + itemsPerRow);
        }
        break;

      case 'ArrowUp':
        if (selectedImageIndex - itemsPerRow >= 0) {
          selectImage(selectedImageIndex - itemsPerRow);
        }
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [images, selectedImageIndex]);

  if (!searchedAlready && !isFetchingData) {
    return null;
  }

  const renderScore = (image) => {
    return (
      <AutoScoreRenderer
        width={30}
        height={14}
        scoresOfWellDrops={[
          {
            wellDropScoreId: image.wellDropScoreId,
            autoScoreValue: image.autoScoreValue,
            autoScoreType: getWellDropScoreType(image.wellDropScoreId),
          },
        ]}
      />
    );
  };

  const renderImagesList = !isFetchingData ? (
    imagesList.map((image, index) => {
      const handleDoubleClick = () =>
        window.open(
          `/exp/${image.experimentId}/${image.plateId}/${image.wellNumber}/${image.dropNumber}/${image.cpid}/Drop/${image.imageBatchId}/drop?from=${NavigationSource.Explorer}`,
          '_blank',
        );
      return image.image.wellImage ? (
        <ImageRenderer
          imageData={image.image.wellImage}
          imageOverlayStyle="absolute top-0 left-0"
          dimensions={{ height: image.image.height, width: ITEM_WIDTH }}
          imageStyle={'h-[70px] flex items-center justify-center cursor-pointer relative'}
          containerStyle={{
            outline: `${selectedImageIndex === index ? '2px solid #F7A246' : ''}`,
            border: '1px solid black',
          }} // for some reason this is not working in tailwind
          onClick={() => selectImage(index)}
          onDoubleClick={handleDoubleClick}
          key={`topImagesThumbnail-${index}`}
          imageOverlay={renderScore(image)}
        />
      ) : (
        <div
          id='thumbnailslistrenderer-renderimagelist-wrapper'
          className="h-[70px] flex w-[69px] relative border border-black cursor-pointer"
          onClick={() => selectImage(index)}
          onDoubleClick={handleDoubleClick}
          style={{
            outline: `${selectedImageIndex === index ? '2px solid #F7A246' : ''}`,
          }}
          key={`top100-${index}`}
        >
          <div id='thumbnailslistrenderer-renderimagelist-content' className="absolute top-0 left-0">
            {renderScore(image)}
            <div id={`thumbnailslistrenderer-renderimagelist-noimage-${index}`} className="ml-2 mt-2">
              <NoImage
                svgProps={{ fill: '#333', width: 24, height: 24 }}
                textClassName="font-italic text-xs"
                key={`top100-noImage-${index}`}
              />
            </div>
          </div>
        </div>
      );
    })
  ) : (
    <LoadingDiv />
  );

  return (
    <div
      id="thumbnails-list-renderer-container"
      ref={containerRef} // Attach the ref to the scrollable container
      className={`flex flex-wrap gap-0.5 overflow-scroll border-t-[1px]`}
      style={{ maxHeight: `${parseFloat(calculateVH(screen.height).toFixed(1))}vh` }}
    >
      {!isFetchingData && imagesList.length === 0 ? <NoDataFound  /> : renderImagesList}
    </div>
  );
};

export default ThumbnailsListRenderer;
